/**
 * Sessions page — lists user sessions with filter, sort, and delete actions
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import sessionService, { SessionResponse } from '../services/sessionService';
import SessionList, { SessionSummary } from '../components/SessionList';

const toSessionSummary = (s: SessionResponse): SessionSummary => ({
  id: s.id,
  name: s.name,
  completion_status: s.is_active ? 'In Progress' : 'Completed',
  is_active: s.is_active,
  created_at: s.created_at,
  updated_at: s.updated_at,
  parent_session_id: s.parent_session_id,
});

const SessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await sessionService.listSessions(true);
      setSessions(data.sessions.map(toSessionSummary));
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          'Failed to load sessions. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const stageToRoute: Record<string, string> = {
    CONTEXT_ENTRY: '/context',
    AGENT_SELECTION: '/agents',
    QA_ACTIVE: '/qa',
    REPORT_GENERATION: '/reports',
    REPORTS_COMPLETE: '/business-plan-qa',
    BUSINESS_PLAN_QA: '/business-plan-qa',
    BUSINESS_PLAN_COMPLETE: '/financial-forecast-qa',
    FINANCIAL_FORECAST_QA: '/financial-forecast-qa',
    COMPLETE: '/reports',
  };

  const handleOpen = async (id: string) => {
    try {
      const { data } = await apiClient.get(`/api/sessions/${id}/stage`);
      const prefix = stageToRoute[data.stage] || '/context';
      navigate(`${prefix}/${id}`);
    } catch {
      // Fallback: if no analysis data exists, start at context
      navigate(`/context/${id}`);
    }
  };

  const handleResume = (id: string) => {
    navigate(`/context/${id}`);
  };

  const handleRename = async (id: string, newName: string) => {
    try {
      await sessionService.updateSession(id, newName);
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, name: newName } : s))
      );
    } catch (err: any) {
      alert(
        err.response?.data?.detail ||
          err.message ||
          'Failed to rename the session. Please try again.'
      );
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this session? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      await sessionService.deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err: any) {
      alert(
        err.response?.data?.detail ||
          err.message ||
          'Failed to delete the session. Please try again.'
      );
    }
  };

  const pageStyle: React.CSSProperties = {
    padding: '40px',
    maxWidth: '900px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '16px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    margin: 0,
  };

  const newBtnStyle: React.CSSProperties = {
    padding: '10px 22px',
    fontSize: '1rem',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background-color 0.2s',
  };

  const backBtnStyle: React.CSSProperties = {
    padding: '8px 16px',
    fontSize: '0.9rem',
    backgroundColor: 'transparent',
    color: '#007bff',
    border: '2px solid #007bff',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  };

  const errorStyle: React.CSSProperties = {
    padding: '12px 16px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: '6px',
    marginBottom: '24px',
  };

  const handleNewAnalysis = async () => {
    try {
      const session = await sessionService.createSession('New Analysis');
      navigate(`/context/${session.id}`);
    } catch (err: any) {
      alert(
        err.response?.data?.detail ||
          err.message ||
          'Failed to create a new session.'
      );
    }
  };

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>My Sessions</h1>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/')}
            style={backBtnStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#007bff';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#007bff';
            }}
          >
            Home
          </button>

          <button
            onClick={handleNewAnalysis}
            style={newBtnStyle}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
          >
            + New Analysis
          </button>
        </div>
      </div>

      {error && (
        <div style={errorStyle} role="alert">
          {error}
          <button
            onClick={fetchSessions}
            style={{
              marginLeft: '16px',
              padding: '4px 12px',
              fontSize: '0.85rem',
              backgroundColor: '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      <SessionList
        sessions={sessions}
        onOpen={handleOpen}
        onResume={handleResume}
        onDelete={handleDelete}
        onRename={handleRename}
        isLoading={isLoading}
      />
    </div>
  );
};

export default SessionsPage;
